import { useEffect, useState, useCallback } from 'react';
import { api, apiError } from '@/lib/api';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

/**
 * UserPermissionsPanel — Panel lateral para gestionar permisos de un usuario.
 * Muestra rutas con checkboxes, origen de permiso, y sección de perfiles.
 *
 * @param {{ userId: string, onClose: () => void }} props
 */
export function UserPermissionsPanel({ userId, onClose }) {
  const [permissions, setPermissions] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [userProfiles, setUserProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedProfileId, setSelectedProfileId] = useState('');

  const fetchPermissions = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/users/${userId}/permissions`);
      setPermissions(data);
      setUserProfiles(data.profiles || []);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [userId]);

  const fetchRoutes = useCallback(async () => {
    try {
      const { data } = await api.get('/route-catalog');
      // API returns object grouped by category - flatten to array
      if (Array.isArray(data)) {
        setRoutes(data);
      } else if (data && typeof data === 'object') {
        setRoutes(Object.values(data).flat());
      } else {
        setRoutes([]);
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  const fetchProfiles = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/profiles');
      setProfiles(data.profiles || data || []);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    Promise.all([fetchPermissions(), fetchRoutes(), fetchProfiles()]).finally(() => setLoading(false));
  }, [userId, fetchPermissions, fetchRoutes, fetchProfiles]);

  const getPermissionForRoute = (routePath) => {
    if (!permissions?.permissions) return null;
    return permissions.permissions.find((p) => p.route?.path === routePath || p.route_path === routePath);
  };

  const hasIndividualOrigin = (perm) => {
    if (!perm) return false;
    const origins = perm.origins || [];
    return origins.includes('individual');
  };

  const isOnlyFromRoleOrProfile = (perm) => {
    if (!perm) return false;
    const origins = perm.origins || [];
    return origins.length > 0 && !origins.includes('individual');
  };

  const handleGrant = async (routePath) => {
    setActionLoading(routePath);
    try {
      await api.post(`/admin/users/${userId}/permissions`, { route_path: routePath });
      toast.success('Permiso concedido');
      await fetchPermissions();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevoke = async (routePath) => {
    setActionLoading(routePath);
    try {
      await api.delete(`/admin/users/${userId}/permissions/${encodeURIComponent(routePath)}`);
      toast.success('Permiso revocado');
      await fetchPermissions();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActionLoading(null);
    }
  };

  const handleAssignProfile = async () => {
    if (!selectedProfileId) return;
    setActionLoading('assign-profile');
    try {
      await api.post(`/admin/users/${userId}/profiles`, { profile_id: selectedProfileId });
      toast.success('Perfil asignado');
      setSelectedProfileId('');
      await fetchPermissions();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveProfile = async (profileId) => {
    setActionLoading(`remove-${profileId}`);
    try {
      await api.delete(`/admin/users/${userId}/profiles/${profileId}`);
      toast.success('Perfil removido');
      await fetchPermissions();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActionLoading(null);
    }
  };

  const renderOriginBadges = (perm) => {
    if (!perm) return null;
    const origins = perm.origins || [];
    return origins.map((origin, idx) => {
      if (origin === 'role_base') {
        return (
          <Badge
            key={idx}
            variant="secondary"
            className="bg-gray-100 text-gray-700 border-gray-200 text-[10px] px-1.5 py-0">
            Rol
          </Badge>
        );
      }
      if (origin.startsWith('profile:')) {
        const profileName = origin.replace('profile:', '');
        return (
          <Badge
            key={idx}
            variant="secondary"
            className="bg-green-50 text-green-700 border-green-200 text-[10px] px-1.5 py-0">
            {profileName}
          </Badge>
        );
      }
      if (origin === 'individual') {
        return (
          <Badge
            key={idx}
            variant="secondary"
            className="bg-orange-50 text-orange-700 border-orange-200 text-[10px] px-1.5 py-0">
            Individual
          </Badge>
        );
      }
      return null;
    });
  };

  // Group routes by category
  const routesByCategory = routes.reduce((acc, route) => {
    const cat = route.category || 'Sin categoría';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(route);
    return acc;
  }, {});

  // Filter profiles not yet assigned
  const availableProfiles = profiles.filter(
    (p) => !userProfiles.some((up) => up.id === p.id || up.profile_id === p.id),
  );

  return (
    <>
      {/* Backdrop — click outside to close */}
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} aria-hidden="true" />
      <aside
        role="complementary"
        aria-label="Panel de permisos del usuario"
        className="fixed right-0 top-0 h-full w-96 bg-background border-l border-border shadow-xl z-50 flex flex-col"
        data-testid="user-permissions-panel">
        {/* Header */}
        <header className="flex items-center justify-between p-4 border-b border-border shrink-0">
          <div>
            <h2 className="text-sm font-semibold">Permisos del usuario</h2>
            {permissions?.user_name && <p className="text-xs text-muted-foreground mt-0.5">{permissions.user_name}</p>}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Cerrar panel">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </Button>
        </header>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-sm text-muted-foreground">Cargando permisos...</p>
          </div>
        ) : (
          <ScrollArea className="flex-1">
            <div className="p-4 space-y-6">
              {/* Section: Route Permissions */}
              <section aria-label="Permisos por ruta">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                  Rutas y permisos
                </h3>
                <div className="space-y-4">
                  {Object.entries(routesByCategory).map(([category, categoryRoutes]) => (
                    <div key={category}>
                      <p className="text-xs font-medium text-muted-foreground mb-2">{category}</p>
                      <div className="space-y-1.5">
                        {categoryRoutes
                          .filter((r) => r.classification === 'private')
                          .map((route) => {
                            const perm = getPermissionForRoute(route.path);
                            const hasIndividual = hasIndividualOrigin(perm);
                            const onlyRoleProfile = isOnlyFromRoleOrProfile(perm);
                            const isChecked = !!perm;
                            const isDisabled = onlyRoleProfile || actionLoading === route.path;

                            return (
                              <div
                                key={route.path}
                                className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-muted/50 group">
                                <Checkbox
                                  id={`perm-${route.path}`}
                                  checked={isChecked}
                                  disabled={isDisabled}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      handleGrant(route.path);
                                    } else if (hasIndividual) {
                                      handleRevoke(route.path);
                                    }
                                  }}
                                  aria-label={`Permiso para ${route.page_name}`}
                                />
                                <div className="flex-1 min-w-0">
                                  <label
                                    htmlFor={`perm-${route.path}`}
                                    className="text-xs font-medium cursor-pointer truncate block">
                                    {route.page_name}
                                  </label>
                                  <span className="text-[10px] text-muted-foreground truncate block">{route.path}</span>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">{renderOriginBadges(perm)}</div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Section: Profile Assignment */}
              <section aria-label="Perfiles asignados">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                  Perfiles asignados
                </h3>

                {/* Current profiles */}
                {userProfiles.length > 0 ? (
                  <div className="space-y-1.5 mb-3">
                    {userProfiles.map((profile) => (
                      <div
                        key={profile.id || profile.profile_id}
                        className="flex items-center justify-between py-1.5 px-2 rounded-md bg-muted/30">
                        <span className="text-xs font-medium">{profile.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[10px] text-destructive hover:text-destructive"
                          onClick={() => handleRemoveProfile(profile.id || profile.profile_id)}
                          disabled={actionLoading === `remove-${profile.id || profile.profile_id}`}>
                          Remover
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground mb-3">Sin perfiles asignados</p>
                )}

                {/* Assign new profile */}
                {availableProfiles.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Select value={selectedProfileId} onValueChange={setSelectedProfileId}>
                      <SelectTrigger className="flex-1 h-8 text-xs">
                        <SelectValue placeholder="Seleccionar perfil..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableProfiles.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      className="h-8 text-xs"
                      onClick={handleAssignProfile}
                      disabled={!selectedProfileId || actionLoading === 'assign-profile'}>
                      Asignar
                    </Button>
                  </div>
                )}
              </section>
            </div>
          </ScrollArea>
        )}
      </aside>
    </>
  );
}
